import type {
  MarketingOpportunityReport,
  MarketingOpportunityReportListItem,
} from '../types';
import { dedupeOpportunityItems } from './marketingOpportunityNormalize';
import { fmtReportDateLabel, localReportDateKey } from './marketingReportDates';

export const COMBINED_OPPORTUNITY_REPORT_PREFIX = '__all__';

export function combinedOpportunityReportId(reportDate: string): string {
  return `${COMBINED_OPPORTUNITY_REPORT_PREFIX}${reportDate}`;
}

export function isCombinedOpportunityReportId(id: string): boolean {
  return id.startsWith(COMBINED_OPPORTUNITY_REPORT_PREFIX);
}

export function combinedOpportunityReportDate(id: string): string | null {
  if (!isCombinedOpportunityReportId(id)) return null;
  const date = id.slice(COMBINED_OPPORTUNITY_REPORT_PREFIX.length);
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null;
}

export function sortOpportunityReportListItems(
  rows: MarketingOpportunityReportListItem[],
): MarketingOpportunityReportListItem[] {
  return [...rows].sort((a, b) => {
    const au = a.updatedAt?.getTime() ?? 0;
    const bu = b.updatedAt?.getTime() ?? 0;
    if (bu !== au) return bu - au;
    return b.report_date.localeCompare(a.report_date) || b.id.localeCompare(a.id);
  });
}

function fmtReportTime(d?: Date): string | null {
  if (!d || Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export type MarketingOpportunityReportOption = MarketingOpportunityReportListItem & {
  optionLabel: string;
  combined?: boolean;
};

/** Build picker rows — inserts an "All runs" synthetic row when a day has multiple radars. */
export function buildOpportunityReportOptions(
  reports: MarketingOpportunityReportListItem[],
  todayKey = localReportDateKey(),
): MarketingOpportunityReportOption[] {
  const sorted = sortOpportunityReportListItems(reports);
  const byDate = new Map<string, MarketingOpportunityReportListItem[]>();
  for (const row of sorted) {
    const group = byDate.get(row.report_date) ?? [];
    group.push(row);
    byDate.set(row.report_date, group);
  }

  const out: MarketingOpportunityReportOption[] = [];
  const seenDates = new Set<string>();

  for (const row of sorted) {
    if (seenDates.has(row.report_date)) continue;
    seenDates.add(row.report_date);

    const group = sortOpportunityReportListItems(byDate.get(row.report_date) ?? [row]);
    const multiple = group.length > 1;
    const isToday = row.report_date === todayKey;

    if (multiple) {
      const totalOpps = group.reduce((sum, r) => sum + r.opportunity_count, 0);
      const dateLabel = fmtReportDateLabel(row.report_date);
      out.push({
        id: combinedOpportunityReportId(row.report_date),
        report_date: row.report_date,
        opportunity_count: totalOpps,
        headline: `All ${group.length} radar runs`,
        combined: true,
        optionLabel: `${dateLabel}${isToday ? ' — Today' : ''} — All runs (${totalOpps} opportunit${totalOpps === 1 ? 'y' : 'ies'})`,
      });
    }

    for (const run of group) {
      const time = fmtReportTime(run.updatedAt ?? run.createdAt);
      const dateLabel = fmtReportDateLabel(run.report_date);
      const headline = run.headline ? ` — ${run.headline.slice(0, 48)}${run.headline.length > 48 ? '…' : ''}` : '';
      out.push({
        ...run,
        optionLabel: multiple
          ? `${dateLabel}${isToday ? ' — Today' : ''}${time ? ` — ${time}` : ''} (${run.opportunity_count} opps)${headline}`
          : `${dateLabel}${isToday ? ' — Today' : ''}${headline}`,
      });
    }
  }

  return out;
}

export function pickPreferredOpportunityReportId(
  reports: MarketingOpportunityReportListItem[],
  previous: string | null,
  preferToday = true,
): string | null {
  const options = buildOpportunityReportOptions(reports);
  if (previous && options.some(o => o.id === previous)) return previous;

  if (preferToday) {
    const today = localReportDateKey();
    const todayOptions = options.filter(o => o.report_date === today);
    if (todayOptions.length) {
      return todayOptions.find(o => o.combined)?.id ?? todayOptions[0].id;
    }
  }

  return options[0]?.id ?? null;
}

/** Merge all radar runs for one calendar day into a single virtual report. */
export function mergeOpportunityReportsForDate(
  reportDate: string,
  reports: MarketingOpportunityReport[],
): MarketingOpportunityReport | null {
  const dayReports = reports
    .filter(r => r.report_date === reportDate)
    .sort((a, b) => (b.updatedAt?.getTime() ?? 0) - (a.updatedAt?.getTime() ?? 0));

  if (!dayReports.length) return null;

  const opportunities = dedupeOpportunityItems(dayReports.flatMap(r => r.opportunities));
  const otherSignals = dedupeOpportunityItems(
    dayReports.flatMap(r => r.otherSignals ?? []),
    opportunities,
  );
  const highlights = Array.from(new Set(dayReports.flatMap(r => r.highlights ?? [])));
  const summaries = dayReports.map(r => r.summary?.trim()).filter(Boolean) as string[];
  const latest = dayReports[0];

  return {
    id: combinedOpportunityReportId(reportDate),
    report_date: reportDate,
    opportunities,
    otherSignals,
    summary:
      summaries.length > 1
        ? `${summaries.length} radar runs combined for ${fmtReportDateLabel(reportDate)}.`
        : summaries[0],
    highlights,
    source: 'marketing-agent',
    createdAt: dayReports[dayReports.length - 1]?.createdAt ?? latest.createdAt,
    updatedAt: latest.updatedAt,
  };
}

export function resolveSelectedOpportunityReport(
  selectedId: string | null,
  reports: MarketingOpportunityReport[],
): MarketingOpportunityReport | null {
  if (!selectedId) return null;

  if (isCombinedOpportunityReportId(selectedId)) {
    const reportDate = combinedOpportunityReportDate(selectedId);
    return reportDate ? mergeOpportunityReportsForDate(reportDate, reports) : null;
  }

  return reports.find(r => r.id === selectedId) ?? null;
}
